// src/components/support/TicketHistory.tsx
import React, { useState, useEffect } from 'react';
import { Form, Badge } from 'react-bootstrap';
import { supportService } from '../../services/supportService';
import type { TicketResponse, Ticket } from '../../types/support';

const ITEMS_PER_PAGE = 10;
const CLOSED_STATUSES = ['resolved', 'closed'];

type TicketStatus = 'new' | 'open' | 'pending' | 'resolved' | 'closed';

const variants: Record<TicketStatus, string> = {
    new: 'primary',
    open: 'info',
    pending: 'warning',
    resolved: 'success',
    closed: 'secondary'
};

interface TicketHistoryProps {
    initialData: TicketResponse | null;
    isLoading: boolean;
    onLoadingChange: (loading: boolean) => void;
}

export const TicketHistory: React.FC<TicketHistoryProps> = ({
                                                                initialData,
                                                                isLoading,
                                                                onLoadingChange
                                                            }) => {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showClosedTickets, setShowClosedTickets] = useState(false);
    const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);

    useEffect(() => {
        // Only use initialData if it exists and we're on page 1
        if (initialData && currentPage === 1) {
            setTickets(initialData.tickets || []);
            setTotalPages(initialData.totalPages || 1);
            onLoadingChange(false);
        } else {
            fetchTickets(currentPage);
        }
    }, [currentPage, initialData]);

    useEffect(() => {
        const filtered = showClosedTickets
            ? tickets
            : tickets.filter(ticket => !CLOSED_STATUSES.includes(ticket.status.toLowerCase()));

        setFilteredTickets(filtered);
        setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
    }, [tickets, showClosedTickets]);

    const fetchTickets = async (page: number) => {
        onLoadingChange(true);
        setError(null);

        try {
            const response = await supportService.getTickets(page, true);
            setTickets(response.tickets || []);
        } catch (err) {
            console.error('Failed to fetch tickets:', err);
            setError('Failed to load tickets');
            setTickets([]);
        } finally {
            onLoadingChange(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const normalizedStatus = status.toLowerCase() as TicketStatus;
        return (
            <Badge bg={variants[normalizedStatus] || 'secondary'} className="text-uppercase">
                {status}
            </Badge>
        );
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const renderPagination = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Ticket history pagination">
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(totalPages)}
                                >
                                    {totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    // Calculate pagination details
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentTickets = filteredTickets.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    const total = filteredTickets.length;

    if (isLoading) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <Form.Check
                    type="switch"
                    id="show-closed"
                    label="Show resolved and closed tickets"
                    checked={showClosedTickets}
                    onChange={(e) => {
                        setShowClosedTickets(e.target.checked);
                        setCurrentPage(1);
                    }}
                />
            </div>

            {currentTickets.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    No tickets found
                </div>
            ) : (
                <>
                    <div className="list-group">
                        {currentTickets.map(ticket => (
                            <div
                                key={ticket.id}
                                className="list-group-item border-0 border-bottom"
                                style={{ cursor: 'default' }}
                            >
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h6 className="mb-1">{ticket.subject}</h6>
                                        <div className="d-flex align-items-center gap-2 text-muted small">
                                            <span>Created {formatDate(ticket.createdAt)}</span>
                                            {ticket.lastResponseAt && (
                                                <>
                                                    <span>•</span>
                                                    <span>Last response {formatDate(ticket.lastResponseAt)}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {getStatusBadge(ticket.status)}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-5">
                        <div className="text-muted small">
                            Showing {startIndex + 1} to {Math.min(startIndex + ITEMS_PER_PAGE, total)} of {total} entries
                        </div>
                        {totalPages > 1 && renderPagination()}
                    </div>
                </>
            )}
        </>
    );
};

export default TicketHistory;